import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { 
  ArrowLeft, 
  Save, 
  Plus, 
  Trash2,
  AlertCircle,
  CheckCircle,
  Code,
  Settings
} from 'lucide-react'
import { Strategy } from '@/types'
import { createStrategy } from '../lib/api'
import Navigation from '../components/Navigation'

export default function NewStrategyPage() {
  const navigate = useNavigate()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [activeStep, setActiveStep] = useState(1)
  const [formData, setFormData] = useState({
    // 基本信息
    name: '',
    name_en: '',
    name_zh: '',
    type: 'trend' as Strategy['type'],
    market: 'crypto' as Strategy['market'],
    market_symbol: '',
    timeframe: '1h' as Strategy['timeframe'],
    status: 'draft' as Strategy['status'],
    description: '',
    description_en: '',
    description_zh: '',
    
    // 策略逻辑
    entry_rules: '',
    entry_rules_en: '',
    entry_rules_zh: '',
    exit_rules: '',
    exit_rules_en: '',
    exit_rules_zh: '',
    risk_management: '',
    risk_management_en: '',
    risk_management_zh: '',
    
    // 参数配置
    parameters: [
      {
        id: '1',
        name: '',
        description: '',
        default_value: '',
        range_min: '',
        range_max: '',
        tunable: true,
        parameter_type: 'number' as const
      }
    ]
  })

  const [errors, setErrors] = useState<Record<string, string>>({})

  const steps = [
    { id: 1, name: '基本信息', icon: Settings },
    { id: 2, name: '策略逻辑', icon: Code },
    { id: 3, name: '参数配置', icon: Settings },
    { id: 4, name: '确认创建', icon: CheckCircle }
  ]

  const strategyTypes = [
    { value: 'trend', label: '趋势跟踪', description: '基于价格趋势的交易策略' },
    { value: 'grid', label: '网格交易', description: '在价格区间内进行网格交易' },
    { value: 'mean_reversion', label: '均值回归', description: '基于价格回归均值的策略' },
    { value: 'breakout', label: '突破策略', description: '基于价格突破的交易策略' },
    { value: 'scalping', label: '高频交易', description: '短期高频交易策略' },
    { value: 'other', label: '其他', description: '其他类型的交易策略' }
  ]

  const markets = [
    { value: 'crypto', label: '加密货币', description: '比特币、以太坊等数字货币' },
    { value: 'forex', label: '外汇', description: '货币对交易' },
    { value: 'stocks', label: '股票', description: '股票市场交易' },
    { value: 'commodities', label: '商品', description: '黄金、原油等商品' },
    { value: 'other', label: '其他', description: '其他交易市场' }
  ]

  const timeframes = [
    { value: '1m', label: '1分钟' },
    { value: '5m', label: '5分钟' },
    { value: '15m', label: '15分钟' },
    { value: '30m', label: '30分钟' },
    { value: '1h', label: '1小时' },
    { value: '4h', label: '4小时' },
    { value: '1d', label: '1天' },
    { value: '1w', label: '1周' },
    { value: '1M', label: '1月' }
  ]

  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {}

    if (step === 1) {
      if (!formData.name.trim()) newErrors.name = '策略名称不能为空'
      if (!formData.market_symbol.trim()) newErrors.market_symbol = '交易对不能为空'
    }

    if (step === 2) {
      if (!formData.entry_rules.trim()) newErrors.entry_rules = '入场规则不能为空'
      if (!formData.exit_rules.trim()) newErrors.exit_rules = '出场规则不能为空'
    }

    if (step === 3) {
      formData.parameters.forEach((param, index) => {
        if (!param.name.trim()) newErrors[`parameters.${index}.name`] = '参数名称不能为空'
        if (!param.default_value.trim()) newErrors[`parameters.${index}.default_value`] = '默认值不能为空'
      })
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep(activeStep + 1)
    }
  }

  const handlePrevious = () => {
    setActiveStep(activeStep - 1)
  }

  const handleSubmit = async () => {
    if (!validateStep(activeStep)) return

    setIsSubmitting(true)
    setError(null)
    
    try {
      // 准备策略数据
      const strategyData = {
        name: formData.name,
        name_en: formData.name_en,
        name_zh: formData.name_zh,
        type: formData.type,
        market: formData.market,
        market_symbol: formData.market_symbol,
        timeframe: formData.timeframe,
        status: formData.status,
        description: formData.description,
        description_en: formData.description_en,
        description_zh: formData.description_zh,
        entry_rules: formData.entry_rules,
        entry_rules_en: formData.entry_rules_en,
        entry_rules_zh: formData.entry_rules_zh,
        exit_rules: formData.exit_rules,
        exit_rules_en: formData.exit_rules_en,
        exit_rules_zh: formData.exit_rules_zh,
        risk_management: formData.risk_management,
        risk_management_en: formData.risk_management_en,
        risk_management_zh: formData.risk_management_zh,
        parameters: formData.parameters
      }

      const result = await createStrategy(strategyData)

      
      // 创建成功后跳转到策略详情页
      navigate(`/strategy/${result.strategy.id}`)
    } catch (err: any) {
      console.error('Error creating strategy:', err)
      setError(err.message || '创建策略失败，请稍后重试')
    } finally {
      setIsSubmitting(false)
    }
  }

  const addParameter = () => {
    setFormData(prev => ({
      ...prev,
      parameters: [
        ...prev.parameters,
        {
          id: Date.now().toString(),
          name: '',
          description: '',
          default_value: '',
          range_min: '',
          range_max: '',
          tunable: true,
          parameter_type: 'number'
        }
      ]
    }))
  }

  const removeParameter = (index: number) => {
    if (formData.parameters.length > 1) {
      setFormData(prev => ({
        ...prev,
        parameters: prev.parameters.filter((_, i) => i !== index)
      }))
    }
  }

  const updateParameter = (index: number, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      parameters: prev.parameters.map((param, i) => 
        i === index ? { ...param, [field]: value } : param
      )
    }))
  }

  const renderStepContent = () => {
    switch (activeStep) {
      case 1:
        return <BasicInfoStep formData={formData} setFormData={setFormData} errors={errors} strategyTypes={strategyTypes} markets={markets} timeframes={timeframes} />
      case 2:
        return <LogicStep formData={formData} setFormData={setFormData} errors={errors} />
      case 3:
        return <ParametersStep formData={formData} addParameter={addParameter} removeParameter={removeParameter} updateParameter={updateParameter} errors={errors} />
      case 4:
        return <ReviewStep formData={formData} />
      default:
        return null
    }
  }

  return (
    <div className="min-h-screen animate-fadein">
      <Navigation />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* 页面标题 */}
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center text-white/70 hover:text-white transition-colors mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            返回
          </button>
          <h1 className="text-3xl font-bold text-gradient">新建策略</h1>
          <p className="text-white/70 mt-2">创建您的交易策略</p>
        </div>

        {/* 步骤指示器 */}
        <div className="card mb-8">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => {
              const Icon = step.icon
              const isActive = step.id === activeStep
              const isCompleted = step.id < activeStep
              
              return (
                <div key={step.id} className="flex items-center">
                  <div className={`flex items-center justify-center w-10 h-10 rounded-full transition-all duration-200 ${
                    isActive 
                      ? 'bg-violet-500 text-white shadow-lg' 
                      : isCompleted 
                        ? 'bg-green-500 text-white' 
                        : 'bg-white/10 text-white/50 border border-white/20'
                  }`}>
                    {isCompleted ? (
                      <CheckCircle className="w-5 h-5" />
                    ) : (
                      <Icon className="w-5 h-5" />
                    )}
                  </div>
                  <span className={`ml-3 text-sm font-medium ${
                    isActive ? 'text-white' : 'text-white/50'
                  }`}>
                    {step.name}
                  </span>
                  {index < steps.length - 1 && (
                    <div className={`w-16 h-0.5 mx-4 ${
                      isCompleted ? 'bg-green-500' : 'bg-white/20'
                    }`} />
                  )}
                </div>
              )
            })}
          </div>
        </div>

        {/* 错误提示 */}
        {error && (
          <div className="card mb-4">
            <div className="flex items-center p-4 bg-red-500/20 border border-red-500/30 rounded-xl">
              <AlertCircle className="w-5 h-5 text-red-400 mr-3" />
              <p className="text-red-400">{error}</p>
            </div>
          </div>
        )}

        {/* 步骤内容 */}
        <div className="card mb-8">
          {renderStepContent()}
        </div>

        {/* 操作按钮 */}
        <div className="flex justify-between">
          <button
            onClick={handlePrevious}
            disabled={activeStep === 1}
            className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            上一步
          </button>
          
          <div className="flex space-x-4">
            {activeStep < steps.length ? (
              <button
                onClick={handleNext}
                className="btn-primary"
              >
                下一步
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="btn-primary"
              >
                {isSubmitting ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    创建中...
                  </div>
                ) : (
                  <div className="flex items-center">
                    <Save className="w-4 h-4 mr-2" />
                    创建策略
                  </div>
                )}
              </button>
            )}
          </div>
        </div>
      </main>
    </div>
  )
}

// 基本信息步骤组件
function BasicInfoStep({ formData, setFormData, errors, strategyTypes, markets, timeframes }: any) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-medium text-ocean-primary mb-4">策略基本信息</h2>
        <p className="text-sm text-ocean-secondary mb-6">填写策略的基本信息，这些信息将用于策略的分类和管理。</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-ocean-primary mb-2">
            策略名称 *
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 bg-white/5 text-white placeholder-white/50 ${
              errors.name ? 'border-red-300' : 'border-white/20'
            }`}
            placeholder="例如: 双均线趋势策略"
          />
          {errors.name && (
            <p className="mt-1 text-sm text-red-600 flex items-center">
              <AlertCircle className="w-4 h-4 mr-1" />
              {errors.name}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            策略类型 *
          </label>
          <select
            value={formData.type}
            onChange={(e) => setFormData({ ...formData, type: e.target.value })}
            className="w-full px-3 py-2 border border-white/20 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 bg-white/5 text-white"
          >
            {strategyTypes.map((type: any) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            交易市场 *
          </label>
          <select
            value={formData.market}
            onChange={(e) => setFormData({ ...formData, market: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            {markets.map((market: any) => (
              <option key={market.value} value={market.value}>
                {market.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            交易对 *
          </label>
          <input
            type="text"
            value={formData.market_symbol}
            onChange={(e) => setFormData({ ...formData, market_symbol: e.target.value })}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
              errors.market_symbol ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="例如: BTC/USD"
          />
          {errors.market_symbol && (
            <p className="mt-1 text-sm text-red-600 flex items-center">
              <AlertCircle className="w-4 h-4 mr-1" />
              {errors.market_symbol}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            时间周期 *
          </label>
          <select
            value={formData.timeframe}
            onChange={(e) => setFormData({ ...formData, timeframe: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            {timeframes.map((tf: any) => (
              <option key={tf.value} value={tf.value}>
                {tf.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            初始状态
          </label>
          <select
            value={formData.status}
            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="draft">草稿</option>
            <option value="test">验证中</option>
            <option value="live">实盘中</option>
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          策略描述
        </label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          rows={4}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          placeholder="简要描述策略的核心逻辑和特点..."
        />
      </div>
    </div>
  )
}

// 策略逻辑步骤组件
function LogicStep({ formData, setFormData, errors }: any) {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-medium text-gray-900 mb-4">策略逻辑说明</h2>
        <p className="text-sm text-gray-600 mb-6">详细描述策略的入场规则、出场规则和风控逻辑。</p>
      </div>

      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            入场规则 *
          </label>
          <textarea
            value={formData.entry_rules}
            onChange={(e) => setFormData({ ...formData, entry_rules: e.target.value })}
            rows={6}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
              errors.entry_rules ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="描述策略的入场条件，支持Markdown格式..."
          />
          {errors.entry_rules && (
            <p className="mt-1 text-sm text-red-600 flex items-center">
              <AlertCircle className="w-4 h-4 mr-1" />
              {errors.entry_rules}
            </p>
          )}
          <p className="mt-1 text-xs text-gray-500">
            支持Markdown格式，可以使用代码块、列表等
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            出场规则 *
          </label>
          <textarea
            value={formData.exit_rules}
            onChange={(e) => setFormData({ ...formData, exit_rules: e.target.value })}
            rows={6}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
              errors.exit_rules ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="描述策略的出场条件，包括止盈止损..."
          />
          {errors.exit_rules && (
            <p className="mt-1 text-sm text-red-600 flex items-center">
              <AlertCircle className="w-4 h-4 mr-1" />
              {errors.exit_rules}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            风控逻辑
          </label>
          <textarea
            value={formData.risk_management}
            onChange={(e) => setFormData({ ...formData, risk_management: e.target.value })}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="描述风险控制措施，如最大仓位、最大回撤等..."
          />
        </div>
      </div>
    </div>
  )
}

// 参数配置步骤组件
function ParametersStep({ formData, addParameter, removeParameter, updateParameter, errors }: any) {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-medium text-gray-900 mb-2">参数配置</h2>
          <p className="text-sm text-gray-600">配置策略运行所需的参数。</p>
        </div>
        <button
          onClick={addParameter}
          className="flex items-center px-3 py-2 text-sm font-medium text-primary-600 hover:text-primary-700"
        >
          <Plus className="w-4 h-4 mr-1" />
          添加参数
        </button>
      </div>

      <div className="space-y-4">
        {formData.parameters.map((param: any, index: number) => (
          <div key={param.id} className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-gray-900">参数 {index + 1}</h3>
              {formData.parameters.length > 1 && (
                <button
                  onClick={() => removeParameter(index)}
                  className="text-red-600 hover:text-red-700"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  参数名称 *
                </label>
                <input
                  type="text"
                  value={param.name}
                  onChange={(e) => updateParameter(index, 'name', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    errors[`parameters.${index}.name`] ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="例如: 移动平均周期"
                />
                {errors[`parameters.${index}.name`] && (
                  <p className="mt-1 text-sm text-red-600">{errors[`parameters.${index}.name`]}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  参数类型
                </label>
                <select
                  value={param.parameter_type}
                  onChange={(e) => updateParameter(index, 'parameter_type', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="number">数值</option>
                  <option value="string">字符串</option>
                  <option value="boolean">布尔值</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  默认值 *
                </label>
                <input
                  type="text"
                  value={param.default_value}
                  onChange={(e) => updateParameter(index, 'default_value', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    errors[`parameters.${index}.default_value`] ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="例如: 20"
                />
                {errors[`parameters.${index}.default_value`] && (
                  <p className="mt-1 text-sm text-red-600">{errors[`parameters.${index}.default_value`]}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  是否可调
                </label>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={param.tunable}
                    onChange={(e) => updateParameter(index, 'tunable', e.target.checked)}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 text-sm text-gray-900">
                    可在回测时调整
                  </label>
                </div>
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                参数描述
              </label>
              <textarea
                value={param.description}
                onChange={(e) => updateParameter(index, 'description', e.target.value)}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="参数的详细说明..."
              />
            </div>

            {param.parameter_type === 'number' && (
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    最小值
                  </label>
                  <input
                    type="number"
                    value={param.range_min}
                    onChange={(e) => updateParameter(index, 'range_min', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="例如: 5"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    最大值
                  </label>
                  <input
                    type="number"
                    value={param.range_max}
                    onChange={(e) => updateParameter(index, 'range_max', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="例如: 100"
                  />
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

// 确认创建步骤组件
function ReviewStep({ formData }: any) {
  const getTypeLabel = (type: string) => {
    const typeMap: Record<string, string> = {
      trend: '趋势跟踪',
      grid: '网格交易',
      mean_reversion: '均值回归',
      breakout: '突破策略',
      scalping: '高频交易',
      other: '其他'
    }
    return typeMap[type] || type
  }

  const getMarketLabel = (market: string) => {
    const marketMap: Record<string, string> = {
      crypto: '加密货币',
      forex: '外汇',
      stocks: '股票',
      commodities: '商品',
      other: '其他'
    }
    return marketMap[market] || market
  }

  const getStatusLabel = (status: string) => {
    const statusMap: Record<string, string> = {
      draft: '草稿',
      test: '验证中',
      live: '实盘中'
    }
    return statusMap[status] || status
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-medium text-gray-900 mb-4">确认创建</h2>
        <p className="text-sm text-gray-600 mb-6">请确认策略信息无误后创建策略。</p>
      </div>

      <div className="bg-gray-50 rounded-lg p-6 space-y-6">
        {/* 基本信息 */}
        <div>
          <h3 className="text-md font-medium text-gray-900 mb-3">基本信息</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500">策略名称:</span>
              <span className="ml-2 font-medium">{formData.name}</span>
            </div>
            <div>
              <span className="text-gray-500">策略类型:</span>
              <span className="ml-2 font-medium">{getTypeLabel(formData.type)}</span>
            </div>
            <div>
              <span className="text-gray-500">交易市场:</span>
              <span className="ml-2 font-medium">{getMarketLabel(formData.market)}</span>
            </div>
            <div>
              <span className="text-gray-500">交易对:</span>
              <span className="ml-2 font-medium">{formData.market_symbol}</span>
            </div>
            <div>
              <span className="text-gray-500">时间周期:</span>
              <span className="ml-2 font-medium">{formData.timeframe}</span>
            </div>
            <div>
              <span className="text-gray-500">初始状态:</span>
              <span className="ml-2 font-medium">{getStatusLabel(formData.status)}</span>
            </div>
          </div>
          {formData.description && (
            <div className="mt-3">
              <span className="text-gray-500 text-sm">描述:</span>
              <p className="mt-1 text-sm">{formData.description}</p>
            </div>
          )}
        </div>

        {/* 策略逻辑 */}
        <div>
          <h3 className="text-md font-medium text-gray-900 mb-3">策略逻辑</h3>
          <div className="space-y-3 text-sm">
            <div>
              <span className="text-gray-500">入场规则:</span>
              <div className="mt-1 p-3 bg-white border border-gray-200 rounded text-sm">
                {formData.entry_rules || '未设置'}
              </div>
            </div>
            <div>
              <span className="text-gray-500">出场规则:</span>
              <div className="mt-1 p-3 bg-white border border-gray-200 rounded text-sm">
                {formData.exit_rules || '未设置'}
              </div>
            </div>
            {formData.risk_management && (
              <div>
                <span className="text-gray-500">风控逻辑:</span>
                <div className="mt-1 p-3 bg-white border border-gray-200 rounded text-sm">
                  {formData.risk_management}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* 参数配置 */}
        <div>
          <h3 className="text-md font-medium text-gray-900 mb-3">参数配置</h3>
          <div className="space-y-2">
            {formData.parameters.map((param: any) => (
              <div key={param.id} className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded text-sm">
                <div>
                  <span className="font-medium">{param.name}</span>
                  <span className="text-gray-500 ml-2">= {param.default_value}</span>
                  {param.description && (
                    <span className="text-gray-500 ml-2">({param.description})</span>
                  )}
                </div>
                <div className="flex items-center space-x-2 text-xs text-gray-500">
                  <span>{param.parameter_type}</span>
                  {param.tunable && <span className="bg-green-100 text-green-800 px-2 py-1 rounded">可调</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
} 